/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import * as ConstructionSiteActions from '@construction-sites/store/construction-sites.actions';
import {
  getCurrentConstructionSite,
  getIsActionPending,
  getTourFilter,
  getTourFilterDate,
  getTours
} from '@construction-sites/store/construction-sites.selectors';
import { State } from '@core/store';
import { Store } from '@ngrx/store';
import { ConstructionSite, Tour } from '@shared/models';
import { Observable, zip } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Component({
    selector: 'app-tour-month-overview',
    templateUrl: './tour-month-overview.component.html',
    styleUrls: ['./tour-month-overview.component.scss'],
    standalone: false
})
export class TourMonthOverviewComponent implements OnInit {
  date$: Observable<Date>;
  constructionSite$: Observable<ConstructionSite>;
  actionPending$: Observable<boolean>;
  tours$: Observable<Tour[]>;

  private destroyRef = inject(DestroyRef);

  constructor(
    private store: Store<State>,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    // Dispatch the tour filter eagerly (outside the template-bound async pipes)
    // so the store is up to date before the first change detection run. Doing
    // it as a side effect inside `tours$` caused an NG0100
    // ExpressionChangedAfterItHasBeenCheckedError, because `date$` was first
    // read as undefined and then changed within the same CD cycle.
    zip(this.route.params, this.route.queryParams).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(([params, queryParams]) => {
        const date = queryParams.date ? new Date(queryParams.date) : new Date();
        const constructionSiteId = params.constructionSiteId;

        this.store.dispatch(new ConstructionSiteActions.SetTourFilter({
          date,
          constructionSiteId
        }));
      })
    ).subscribe();

    this.tours$ = this.store.select(getTours);
    this.date$ = this.store.select(getTourFilterDate);
    this.constructionSite$ = this.store.select(getCurrentConstructionSite);
    this.actionPending$ = this.store.select(getIsActionPending);
  }

  viewPreviousMonth(): void {
    this.store.select(getTourFilter).pipe(
      map(filter => filter.date),
      tap(date => this.store.dispatch(new ConstructionSiteActions.SetTourFilter({
        date: new Date(date.setMonth(date.getMonth() - 1))
      })))
    ).subscribe();
  }

  viewNextMonth(): void {
    this.store.select(getTourFilter).pipe(
      map(filter => filter.date),
      tap(date => this.store.dispatch(new ConstructionSiteActions.SetTourFilter({
        date: new Date(date.setMonth(date.getMonth() + 1))
      })))
    ).subscribe();
  }
}
